import { useState, useEffect, useCallback, useRef } from 'react';
import { findDOMNode } from 'react-dom';

export const useTableKeyboardNavigation = (data, colCount, externalTableRef) => {
    const [focusedCell, setFocusedCell] = useState({ rowIndex: 0, colIndex: 0 });
    const [isEditing, setIsEditing] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const cellRefs = useRef(new Map());

    // Безопасное получение размеров таблицы
    let rowCount = 0;

    if (data && data.length) {
        rowCount = data.length;
    }
    const isTableReady = rowCount > 0 && colCount > 0;

    const getTableElement = useCallback(() => {
        if (!externalTableRef || !externalTableRef.current) return null;

        const element = externalTableRef.current;

        if (element && element.addEventListener) {
            return element;
        }

        try {
            const domElement = findDOMNode(element);
            if (domElement && domElement.addEventListener) {
                return domElement;
            }
        } catch (error) {
        }

        return null;
    }, [externalTableRef]);

    useEffect(() => {
        if (isTableReady && !isInitialized) {
            setIsInitialized(true);
        } else if (!isTableReady && isInitialized) {
            setIsInitialized(false);
            setIsEditing(false);
        }
    }, [isTableReady, isInitialized, rowCount, colCount]);

    const registerCellRef = useCallback((rowIndex, colIndex, ref) => {
        if (!isTableReady) return;

        const key = `${rowIndex}-${colIndex}`;
        if (ref) {
            cellRefs.current.set(key, ref);
        } else {
            cellRefs.current.delete(key);
        }
    }, [isTableReady]);

    const getCurrentCellRef = useCallback(() => {
        if (!isTableReady || !isInitialized) return null;

        const key = `${focusedCell.rowIndex}-${focusedCell.colIndex}`;
        return cellRefs.current.get(key);
    }, [focusedCell, isTableReady, isInitialized]);

    const focusCell = useCallback(() => {
        if (!isTableReady || !isInitialized) return;

        const cellRef = getCurrentCellRef();
        if (cellRef) {
            let focusableElement = cellRef.querySelector('input, textarea, [tabindex], button');
            if (!focusableElement) {
                focusableElement = cellRef;
            }

            if (focusableElement && focusableElement.focus) {
                focusableElement.focus();
                if (focusableElement.tagName === 'INPUT' && focusableElement.type === 'text') {
                    focusableElement.select();
                }
            }
        }
    }, [getCurrentCellRef, isTableReady, isInitialized]);

    const moveFocus = useCallback((direction) => {
        if (!isTableReady || !isInitialized || isEditing || rowCount === 0 || colCount === 0) {
            return;
        }

        setFocusedCell(prev => {
            let rowIndex = prev.rowIndex;
            let colIndex = prev.colIndex;

            if (direction === 'up') {
                rowIndex = Math.max(0, rowIndex - 1);
            } else if (direction === 'down') {
                rowIndex = Math.min(rowCount - 1, rowIndex + 1);
            } else if (direction === 'left') {
                colIndex = Math.max(0, colIndex - 1);
            } else if (direction === 'right') {
                colIndex = Math.min(colCount - 1, colIndex + 1);
            } else {
                return prev;
            }

            if (rowIndex < 0 || rowIndex >= rowCount || colIndex < 0 || colIndex >= colCount) {
                return prev;
            }

            return { rowIndex, colIndex };
        });
    }, [rowCount, colCount, isEditing, isTableReady, isInitialized]);

    const startEditing = useCallback(() => {
        if (!isTableReady || !isInitialized || isEditing) return;

        setIsEditing(true);
        setTimeout(() => {
            focusCell();
        }, 0);
    }, [isEditing, focusCell, isTableReady, isInitialized]);
    const stopEditing = useCallback(() => {
        if (!isTableReady || !isInitialized || !isEditing) return;

        setIsEditing(false);
        const tableElement = getTableElement();
        if (tableElement && tableElement.focus) {
            tableElement.focus();
        }
    }, [isEditing, isTableReady, isInitialized, getTableElement]);

    const handleTableKeyDown = useCallback((e) => {
        if (!isTableReady || !isInitialized) return;

        const tableElement = getTableElement();
        if (!tableElement) return;

        if (e.key === 'ArrowUp') {
            e.preventDefault();
            moveFocus('up');
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            moveFocus('down');
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            moveFocus('left');
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            moveFocus('right');
        } else if (e.key === 'Enter') {
            e.preventDefault();
            startEditing();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            stopEditing();
        } else if (e.key === 'Tab') {
            e.preventDefault();
            moveFocus(e.shiftKey ? 'left' : 'right');
        } else {
            if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                startEditing();
            }
        }
    }, [moveFocus, startEditing, stopEditing, isTableReady, isInitialized, getTableElement]);
    const handleCellKeyDown = useCallback((e) => {
        if (!isTableReady || !isInitialized) return;

        if (e.key === 'Enter') {
            e.preventDefault();
            stopEditing();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            stopEditing();
        } else if (e.key === 'Tab') {
            e.preventDefault();
            stopEditing();
            moveFocus(e.shiftKey ? 'left' : 'right');
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            let allowNavigation = false;
            if (e.target.selectionStart === 0) {
                allowNavigation = true;
            } else if (e.target.value && e.target.selectionStart === e.target.value.length) {
                allowNavigation = true;
            }

            if (allowNavigation) {
                e.preventDefault();
                stopEditing();
                moveFocus(e.key === 'ArrowUp' ? 'up' : 'down');
            }
        }
    }, [stopEditing, moveFocus, isTableReady, isInitialized]);

    useEffect(() => {
        if (!isTableReady || !isInitialized) return;

        const tableElement = getTableElement();
        if (tableElement && tableElement.addEventListener) {
            tableElement.addEventListener('keydown', handleTableKeyDown);
            return () => tableElement.removeEventListener('keydown', handleTableKeyDown);
        } else {
            console.warn('Table element is not a DOM node or not ready:', tableElement);
        }
    }, [handleTableKeyDown, isTableReady, isInitialized, getTableElement]);

    useEffect(() => {
        if (!isTableReady || !isInitialized || isEditing) return;
        const timeoutId = setTimeout(() => {
            const tableElement = getTableElement();
            if (tableElement && tableElement.focus) {
                tableElement.focus();
            }
        }, 0);

        return () => clearTimeout(timeoutId);
    }, [focusedCell, isEditing, isTableReady, isInitialized, getTableElement]);

    useEffect(() => {
        if (!isTableReady) {
            cellRefs.current.clear();
        }
    }, [isTableReady]);

    let returnFocusedCell = { rowIndex: 0, colIndex: 0 };
    let returnIsEditing = false;

    if (isInitialized) {
        returnFocusedCell = focusedCell;
        returnIsEditing = isEditing;
    }

    return {
        focusedCell: returnFocusedCell,
        isEditing: returnIsEditing,
        isInitialized,
        isTableReady,
        setFocusedCell,
        setIsEditing,
        registerCellRef,
        handleCellKeyDown,
        moveFocus,
        startEditing,
        stopEditing,
        debugInfo: {
            rowCount,
            colCount: colCount,
            isTableReady,
            isInitialized,
            cellRefsCount: cellRefs.current.size,
            hasTableElement: !!getTableElement()
        }
    };
};
